import React, { useState, useEffect } from "react";
import { db, doc, updateDoc } from "../../../firebaseConfig";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { v4 as uuidv4 } from "uuid";
import { useAtom } from "jotai";
import { themeAtom, isAdminAtom } from "../../../-Atoms";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import { motion } from "framer-motion";

const ModelsContainer = ({ jobData, setHasUnsavedChanges }) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openPicker, setOpenPicker] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const autosizeOptions = {
    columns: ["name", "agency"],
  };

  const [models, setModels] = useState(jobData?.models || []);
  useEffect(() => {
    setModels(
      Array.isArray(jobData?.models)
        ? jobData.models.map((model) => ({
            ...model,
            id: model.id || generateUniqueId(model),
          }))
        : []
    );
  }, [jobData.models, setModels]);
  
  // Handle focus state for unsaved changes tracking
  useEffect(() => {
    if (isFocused && setHasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  }, [isFocused, setHasUnsavedChanges]);

  const generateUniqueId = (model) => {
    return (
      model.name +
      model.agency +
      model.callTime +
      model.finishTime +
      model.bookedBy +
      model.paidBy +
      model.faceNoFace
    );
  };

  const updateDatabase = async (updatedModels) => {
    try {
      await updateDoc(doc(db, "JobData", jobData.id), {
        models: updatedModels,
      });
      console.log("Database updated with new models.");
    } catch (error) {
      console.error("Error updating database:", error);
    }
  };

  const handleAddModel = () => {
    if (!isAdmin) return;
    const newModel = {
      id: uuidv4(),
      name: "",
      agency: "",
      callTime: null,
      finishTime: null,
      insta: "",
    };
    const updatedModels = [...models, newModel];
    setModels(updatedModels);
    updateDatabase(updatedModels);
    console.log("Updated Models:", updatedModels);
  };

  const handleDeleteModels = () => {
    if (!isAdmin || rowSelectionModel.length === 0) return;
    const updatedModels = models.filter(
      (model) => !rowSelectionModel.includes(model.id)
    );
    setModels(updatedModels);
    updateDatabase(updatedModels);
    console.log("Deleted Models:", updatedModels);
    setRowSelectionModel([]);
  };

  const handleTimeChange = (newValue, rowId, timeType) => {
    const newTime = newValue?.toISOString();
    setModels((prevModels) => {
      const updatedModels = prevModels.map((model) =>
        model.id === rowId ? { ...model, [timeType]: newTime } : model
      );
      updateDatabase(updatedModels);
      return updatedModels;
    });
    setOpenPicker(null);
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };

  const handleTimePickerOpen = (rowId, timeType, defaultTime) => {
    setOpenPicker(timeType);
    setIsFocused(true);
    setModels((prevModels) => {
      const currentModel = prevModels.find((model) => model.id === rowId);
      if (!currentModel[timeType]) {
        const updatedModels = prevModels.map((model) =>
          model.id === rowId
            ? { ...model, [timeType]: defaultTime.toISOString() }
            : model
        );
        updateDatabase(updatedModels);

        return updatedModels;
      }
      return prevModels;
    });
  };

  const columns = [
    { field: "name", headerName: "Name", minWidth: 120, editable: isAdmin },
    { field: "agency", headerName: "Agency", width: 115, editable: isAdmin },
    {
      field: "callTime",
      headerName: "Call Time",
      width: 148,
      renderCell: (params) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={params.row.callTime ? dayjs(params.row.callTime) : null}
            disabled={openPicker === "finishTime"}
            onOpen={() =>
              handleTimePickerOpen(
                params.row.id,
                "callTime",
                dayjs().hour(8).minute(30)
              )
            }
            onClose={() => {
              setOpenPicker(null);
              setIsFocused(false);
              if (setHasUnsavedChanges) {
                setHasUnsavedChanges(false);
              }
            }}
            onChange={(newValue) =>
              handleTimeChange(newValue, params.row.id, "callTime")
            }
            onAccept={(newValue) =>
              handleTimeChange(newValue, params.row.id, "callTime")
            }
            slotProps={{
              textField: {
                size: "small",
                variant: "filled",
                InputProps: { disableUnderline: true },
                sx: {
                  backgroundColor: 
                    theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 5px rgba(0,0,0,0.2)"
                      : "0 2px 5px rgba(0,0,0,0.05)",
                  borderRadius: "8px"
                }
              },
            }}
          />
        </LocalizationProvider>
      ),
      editable: isAdmin,
    },
    {
      field: "finishTime",
      headerName: "Finish Time",
      width: 148,
      renderCell: (params) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            value={params.row.finishTime ? dayjs(params.row.finishTime) : null}
            disabled={openPicker === "callTime"}
            onOpen={() =>
              handleTimePickerOpen(
                params.row.id,
                "finishTime",
                dayjs().hour(16).minute(30)
              )
            }
            onClose={() => {
              setOpenPicker(null);
              setIsFocused(false);
              if (setHasUnsavedChanges) {
                setHasUnsavedChanges(false);
              }
            }}
            onChange={(newValue) =>
              handleTimeChange(newValue, params.row.id, "finishTime")
            }
            onAccept={(newValue) =>
              handleTimeChange(newValue, params.row.id, "finishTime")
            }
            slotProps={{
              textField: {
                size: "small",
                variant: "filled",
                InputProps: { disableUnderline: true },
                sx: {
                  backgroundColor: 
                    theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 5px rgba(0,0,0,0.2)"
                      : "0 2px 5px rgba(0,0,0,0.05)",
                  borderRadius: "8px"
                }
              },
            }}
          />
        </LocalizationProvider>
      ),
      editable: isAdmin,
    },
    { field: "insta", headerName: "Insta", width: 100, editable: true },
  ];
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  return (
    <FormControl
      fullWidth
      sx={{
        position: "relative",
      }}
      variant="filled"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color:
                  theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.8)",
                fontWeight: "bold",
                fontSize: "1.1rem",
              }}
            >
              Models
            </Typography>
            <Box sx={{ display: isAdmin ? "flex" : "none" }}>
              <motion.div
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton 
                  onClick={handleAddModel}
                  sx={{
                    backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(245,245,245,0.9)",
                    '&:hover': {
                      backgroundColor: theme === "dark" ? "rgba(55,55,55,0.9)" : "rgba(235,235,235,1)",
                    }
                  }}
                >
                  <AddIcon />
                </IconButton>
              </motion.div>
              <motion.div
                variants={itemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton
                  onClick={handleDeleteModels}
                  disabled={rowSelectionModel.length === 0}
                  sx={{
                    ml: 1,
                    backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(245,245,245,0.9)",
                    '&:hover': {
                      backgroundColor: theme === "dark" ? "rgba(55,55,55,0.9)" : "rgba(235,235,235,1)",
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </motion.div>
            </Box>
          </Box>
          <Box
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
                backgroundColor: theme === "dark" ? "rgba(45,45,45,0.5)" : "rgba(250,250,250,0.5)",
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: theme === "dark" ? "0 2px 5px rgba(0,0,0,0.2)" : "0 2px 5px rgba(0,0,0,0.05)",
              },
              '& .MuiDataGrid-cell': {
                borderBottom: theme === "dark" ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: theme === "dark" ? "rgba(40,40,40,0.9)" : "rgba(240,240,240,0.9)",
                borderBottom: 'none',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
            }}
          >
            <DataGrid
              rows={models}
              columns={columns}
              checkboxSelection={isAdmin}
              disableRowSelectionOnClick
              autosizeOnMount
              autosizeOptions={autosizeOptions}
              rowSelectionModel={rowSelectionModel}
              disableColumnSorting
              onRowSelectionModelChange={(newSelection) => {
                setRowSelectionModel(newSelection);
              }}
              processRowUpdate={(updatedRow) => {
                setIsFocused(true);
                const updatedModels = models.map((model) =>
                  model.id === updatedRow.id ? updatedRow : model
                );
                setModels(updatedModels);
                updateDatabase(updatedModels);
                if (setHasUnsavedChanges) {
                  setHasUnsavedChanges(false);
                }
                return updatedRow;
              }}
              onProcessRowUpdateError={(error) => console.error(error)}
              getRowId={(row) => row.id || generateUniqueId(row)}
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
              }}
              onCellEditStart={() => {
                setIsFocused(true);
                if (setHasUnsavedChanges) {
                  setHasUnsavedChanges(true);
                }
              }}
              onCellEditStop={() => {
                setIsFocused(false);
                if (setHasUnsavedChanges) {
                  setHasUnsavedChanges(false);
                }
              }}
            />
          </Box>
        </motion.div>
      </Box>
    </FormControl>
  );
};

export default ModelsContainer;
